import { HttpErrorResponse } from '@angular/common/http';
import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatChipsModule } from '@angular/material/chips';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSelectModule } from '@angular/material/select';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { TranslocoPipe } from '@jsverse/transloco';
import { __, CardComponent, MessengerService, TableService, TableToolboxComponent } from '@web/birdz-angular';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, GridOptions, GridReadyEvent, ICellRendererParams } from 'ag-grid-community';
import { ModalDuplicateQueryComponent } from 'app/components/modal-duplicate-query/modal-duplicate-query.component';
import { ModalExecutedSqlComponent } from 'app/components/modal-executed-sql/modal-executed-sql.component';
import { QueryExecutionResult } from 'app/interfaces/query-execution-result.interface';
import { Query } from 'app/interfaces/query.interface';
import { TemplateField } from 'app/interfaces/template-field.interface';
import { QueryService } from 'app/services/query.service';
import { UserService } from 'app/services/user.service';
import { formatBytes } from 'app/utils/format-bytes.utils';
import { parseLinkFields, parseTemplateFields } from 'app/utils/template-parser.utils';
import { format as fnsFormat, isValid as fnsIsValid, parse as fnsParse } from 'date-fns';
import { catchError, debounceTime, filter, of, startWith, switchMap, tap } from 'rxjs';

@Component({
  selector: 'app-page-query-execute',
  templateUrl: './page-query-execute.component.html',
  styleUrls: ['./page-query-execute.component.scss'],
  imports: [
    CardComponent,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatChipsModule,
    MatFormFieldModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatDatepickerModule,
    RouterLink,
    TranslocoPipe,
    TableToolboxComponent,
    AgGridAngular,
  ],
})
export class PageQueryExecuteComponent implements OnInit {
  private queryService = inject(QueryService);
  private userService = inject(UserService);
  private messenger = inject(MessengerService);
  private table = inject(TableService);
  private dialog = inject(MatDialog);
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private fb = inject(FormBuilder);
  private destroyRef = inject(DestroyRef);

  query: Query | null = null;
  isLoading = false;
  isExecuting = false;
  isExportingCsv = false;
  isExportingJson = false;
  result: QueryExecutionResult | null = null;
  executionError: { message: string; sql: string | null } | null = null;
  estimatedBytes: number | null = null;
  isEstimating = false;
  templateFields: TemplateField[] = [];
  templateForm: FormGroup = this.fb.group({});
  isEditor = false;
  currentUserEmail = '';
  isDescriptionExpanded = false;
  /** Maps column alias → link label for columns defined with {link} tags. */
  linkColumnMap: Record<string, string> = {};

  gridOptions: GridOptions = this.table.getDefaultGridOptions('clientSide', {
    getRowId: (params) => String(params.data.$rowIdx ?? ''),
    suppressMovableColumns: true,
  });
  gridApi: GridApi | undefined = undefined;
  agColumns: ColDef[] = [];
  agRows: QueryExecutionResult['rows'] = [];

  get displayedColumns(): string[] {
    return this.result?.columns ?? [];
  }

  get formattedBytesProcessed(): string | null {
    return formatBytes(this.result?.bytes_processed ?? null);
  }

  get formattedEstimatedBytes(): string | null {
    return formatBytes(this.estimatedBytes);
  }

  get isDeleted(): boolean {
    return !!this.query?.deleted_at;
  }

  ngOnInit(): void {
    this.isEditor = this.userService.getUser()?.can('query_editor') ?? false;
    this.currentUserEmail = this.userService.getUser()?.getEmail() ?? '';

    this.route.params.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      const id = params['id'];
      if (id) {
        this.loadQuery(id);
      }
    });
  }

  private loadQuery(id: string): void {
    this.isLoading = true;
    this.queryService.getQuery(id).subscribe({
      next: (query) => {
        this.isLoading = false;
        this.query = query;
        this.parseTemplate();
        this.setupEstimation();

        if (query.auto_execute && this.templateFields.length === 0) {
          this.execute();
        }
      },
      error: () => {
        this.isLoading = false;
        this.router.navigate(['/queries']);
      },
    });
  }

  private parseTemplate(): void {
    if (!this.query) {
      return;
    }
    this.templateFields = parseTemplateFields(this.query.sql);

    this.linkColumnMap = {};
    for (const lf of parseLinkFields(this.query.sql)) {
      if (lf.columnName) {
        this.linkColumnMap[lf.columnName] = lf.label;
      }
    }

    const urlParams = this.route.snapshot.queryParams;
    const controls: Record<string, [Date | null] | [string]> = {};
    for (const field of this.templateFields) {
      const urlValue = urlParams[field.name] as string | undefined;
      if (field.type === 'date') {
        let dateVal: Date | null = null;
        if (urlValue) {
          const p = fnsParse(urlValue, 'yyyy-MM-dd', new Date());
          dateVal = fnsIsValid(p) ? p : null;
        } else if (field.default) {
          const p = fnsParse(field.default, 'dd-MM-yyyy', new Date());
          dateVal = fnsIsValid(p) ? p : null;
        }
        controls[field.name] = [dateVal];
      } else if (field.type === 'env') {
        controls[field.name] = [urlValue ?? field.default ?? field.values?.at(-1) ?? 'prd'];
      } else {
        controls[field.name] = [urlValue ?? field.default ?? ''];
      }
    }
    this.templateForm = this.fb.group(controls);
  }

  private buildTemplateValues(): Record<string, string | null> {
    const raw = this.templateForm.value as Record<string, string | number | Date | null>;
    const values: Record<string, string | null> = {};
    for (const field of this.templateFields) {
      const v = raw[field.name];
      if (field.type === 'date') {
        values[field.name] = v instanceof Date ? fnsFormat(v, 'yyyy-MM-dd') : null;
      } else if (field.type === 'number') {
        values[field.name] = v !== null && v !== undefined && v !== '' ? String(v) : null;
      } else if (typeof v === 'string' && v.trim().length > 0) {
        values[field.name] = v;
      } else {
        values[field.name] = null;
      }
    }
    return values;
  }

  execute(): void {
    if (!this.query) {
      return;
    }

    const values = this.buildTemplateValues();

    if (this.templateFields.length > 0) {
      const queryParams = Object.fromEntries(Object.entries(values).filter(([, v]) => v !== null));
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams,
        replaceUrl: true,
      });
    }

    this.isExecuting = true;
    this.result = null;
    this.executionError = null;

    this.queryService.executeQuery(this.query.id, values, 'html').subscribe({
      next: (res) => {
        this.isExecuting = false;
        this.result = res;
        this.updateGrid();
      },
      error: (err: HttpErrorResponse) => {
        this.isExecuting = false;
        this.executionError = {
          message: err.error?.message ?? err.message ?? 'Erreur inconnue',
          sql: err.error?.rendered_sql ?? null,
        };
      },
    });
  }

  private setupEstimation(): void {
    if (this.query?.database_id !== 'bigquery' || this.isDeleted) {
      return;
    }

    this.templateForm.valueChanges
      .pipe(
        startWith(this.templateForm.value),
        debounceTime(400),
        filter(() => this.areAllFieldsFilled()),
        tap(() => {
          this.isEstimating = true;
        }),
        switchMap(() => {
          const values = this.buildTemplateValues();
          return this.queryService.estimateQuery(this.query!.id, values).pipe(catchError(() => of(null)));
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe((res) => {
        this.isEstimating = false;
        if (res) {
          this.estimatedBytes = res.bytes_processed;
        }
      });
  }

  private areAllFieldsFilled(): boolean {
    const raw = this.templateForm.value as Record<string, string | number | Date | null>;
    return this.templateFields.every((field) => {
      const v = raw[field.name];
      if (field.type === 'date') {
        return v instanceof Date;
      }
      if (field.type === 'env') {
        return true;
      }
      if (field.type === 'number') {
        return v !== null && v !== undefined && v !== '';
      }
      return typeof v === 'string' && v.trim().length > 0;
    });
  }

  onGridReady(event: GridReadyEvent): void {
    this.gridApi = event.api ?? undefined;
  }

  private updateGrid(): void {
    if (!this.result) {
      this.agColumns = [];
      this.agRows = [];
      return;
    }

    this.agColumns = this.result.columns.map((col) => {
      const linkLabel = this.linkColumnMap[col];
      if (linkLabel) {
        return {
          field: col,
          headerName: linkLabel,
          sortable: true,
          cellRenderer: (params: ICellRendererParams) => {
            const url = String(params.value ?? '');
            if (!url || !/^https?:\/\//i.test(url)) {
              return '';
            }
            const a = document.createElement('a');
            a.href = url;
            a.target = '_blank';
            a.rel = 'noopener noreferrer';
            a.textContent = linkLabel; // textContent encode automatiquement le HTML
            return a.outerHTML;
          },
        };
      }
      return { field: col, headerName: col, sortable: true };
    });

    this.agRows = this.result.rows.map((row, i) => ({ ...row, $rowIdx: i }));
  }

  toggleFavorite(): void {
    if (!this.query) {
      return;
    }
    this.queryService.toggleFavorite(this.query.id).subscribe((res) => {
      this.query!.is_favorite = res.is_favorite;
      this.messenger.success(__(res.is_favorite ? 'QUERY_FAVORITE_ADDED' : 'QUERY_FAVORITE_REMOVED'));
    });
  }

  showExecutedSql(): void {
    if (!this.result?.rendered_sql) {
      return;
    }
    this.dialog.open(ModalExecutedSqlComponent, {
      data: this.result.rendered_sql,
      width: '720px',
      maxHeight: '85vh',
    });
  }

  openDuplicateModal(): void {
    if (!this.query) {
      return;
    }
    this.dialog
      .open(ModalDuplicateQueryComponent, { data: { name: this.query.name }, width: '480px' })
      .afterClosed()
      .pipe(
        tap((newName: string | null) => {
          if (!newName) {
            return;
          }
          this.queryService
            .duplicateQuery(this.query!.id, newName)
            .pipe(
              tap((newQuery) => {
                this.messenger.success(__('QUERY_DUPLICATED'));
                this.router.navigate(['/query', newQuery.id]);
              }),
              takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({ error: () => this.messenger.error(__('ERROR_OCCURRED')) });
        })
      )
      .subscribe();
  }

  downloadCsv(): void {
    this.isExportingCsv = true;

    if (this.query?.database_id === 'bigquery') {
      if (!this.query) {
        this.isExportingCsv = false;
        return;
      }
      const values = this.buildTemplateValues();
      this.queryService
        .exportQueryToCsv(this.query.id, values)
        .pipe(this.messenger.showFriendlyErrorMessage())
        .subscribe({
          next: ({ url }) => {
            this.isExportingCsv = false;
            const a = document.createElement('a');
            a.href = url;
            a.download = `${this.sanitizeFilename(this.query?.name ?? 'export')}.csv`;
            a.click();
          },
          error: () => {
            this.isExportingCsv = false;
          },
        });
      return;
    }

    this.runExport(
      'csv',
      (res) => {
        const header = res.columns.join(';');
        const rows = res.rows.map((row) => res.columns.map((col) => `${row[col] ?? ''}`).join(';'));
        this.triggerDownload(
          [header, ...rows].join('\n'),
          'text/csv;charset=utf-8;',
          `${this.sanitizeFilename(this.query?.name ?? 'export')}.csv`
        );
      },
      () => {
        this.isExportingCsv = false;
      }
    );
  }

  downloadJson(): void {
    this.isExportingJson = true;
    this.runExport(
      'json',
      (res) => {
        this.triggerDownload(
          JSON.stringify(res.rows, null, 2),
          'application/json',
          `${this.sanitizeFilename(this.query?.name ?? 'export')}.json`
        );
      },
      () => {
        this.isExportingJson = false;
      }
    );
  }

  private runExport(
    format: 'csv' | 'json',
    onSuccess: (res: QueryExecutionResult) => void,
    onFinish: () => void
  ): void {
    if (!this.query) {
      onFinish();
      return;
    }

    const values = this.buildTemplateValues();

    this.queryService
      .executeQuery(this.query.id, values, format)
      .pipe(this.messenger.showFriendlyErrorMessage())
      .subscribe({
        next: (res) => {
          onFinish();
          onSuccess(res);
        },
        error: () => {
          onFinish();
        },
      });
  }

  private triggerDownload(content: string, mimeType: string, filename: string): void {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }

  private sanitizeFilename(name: string): string {
    return name.replace(/[^a-z0-9_-]/gi, '_');
  }
}
